import React, { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { StatusBadge } from '../components/Badges';
import { Button } from '../components/ui/button';
import {
  Receipt, DollarSign, Clock, CheckCircle2, AlertTriangle, XCircle, Wallet,
  Zap, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';

// Palette rule: blue for pending/active, gold for premium, green ONLY for successful money movement.
const PAYOUT_STATUS = {
  pending:     { color: 'bg-gray-50 text-gray-700 border-gray-200',       icon: Clock,         label: 'Pending' },
  approved:    { color: 'bg-blue-50 text-blue-800 border-blue-200',       icon: CheckCircle2,  label: 'Approved · Awaiting Transfer' },
  transferred: { color: 'bg-blue-50 text-[#0044CC] border-blue-200',      icon: Zap,           label: 'Transferred to Stripe' },
  paid:        { color: 'bg-green-50 text-green-800 border-green-200',    icon: CheckCircle2,  label: 'Paid Out' },
  reversed:    { color: 'bg-gray-100 text-gray-700 border-gray-300',      icon: XCircle,       label: 'Reversed (Refund)' },
  disputed:    { color: 'bg-red-50 text-red-700 border-red-200',          icon: AlertTriangle, label: 'Disputed · On Hold' },
  cancelled:   { color: 'bg-gray-50 text-gray-700 border-gray-200',       icon: XCircle,       label: 'Cancelled' },
};

export default function PayoutsPage() {
  const { user } = useAuth();
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [credits, setCredits] = useState([]);
  const [connect, setConnect] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [earnings, setEarnings] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        if (user?.role === 'seller') {
          const [pRes, cRes, eRes] = await Promise.all([
            api.get('/payouts/my'),
            api.get('/connect/status').catch(() => ({ data: { connected: false, onboarding_needed: true } })),
            api.get('/seller/earnings').catch(() => ({ data: null })),
          ]);
          setPayouts(pRes.data);
          setConnect(cRes.data);
          setEarnings(eRes.data);
        }
        if (user?.role === 'buyer') {
          const res = await api.get('/credits/my');
          setCredits(res.data);
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, [user]);

  const startOnboarding = async () => {
    setConnecting(true);
    try {
      const res = await api.post('/connect/onboard', { origin_url: window.location.origin });
      if (res.data.onboarding_url) {
        window.location.href = res.data.onboarding_url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Onboarding failed');
      setConnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="text-center py-24 text-gray-500 font-mono text-sm">Loading...</div>
      </div>
    );
  }

  const totalPending = payouts.filter(p => p.status === 'pending').reduce((s, p) => s + (p.net_amount || 0), 0);
  const totalPaid = payouts.filter(p => p.status === 'paid' || p.status === 'transferred').reduce((s, p) => s + (p.net_amount || 0), 0);
  const totalCredits = credits.filter(c => c.status === 'issued').reduce((s, c) => s + (c.amount || 0), 0);
  const isConnected = connect?.connected && connect?.payouts_enabled;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-8">
        {user?.role === 'seller' && (
          <>
            <h1 className="font-heading text-3xl font-semibold tracking-tight text-gray-900">Marketplace Payouts</h1>
            <p className="text-sm text-gray-600 mt-1">
              Net amounts (80% after platform fee) owed to you for each purchase. Once transferred, Stripe pays your bank in 2-7 business days.
            </p>

            {/* Stripe Connect card */}
            <div
              className={`mt-6 mb-6 border rounded-lg p-5 flex items-start gap-4 ${
                isConnected ? 'border-blue-200 bg-blue-50/40' : 'border-gray-200 bg-gray-50'
              }`}
              data-testid="stripe-connect-card"
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isConnected ? 'bg-[#0055FF] text-white' : 'bg-white border border-gray-300 text-gray-500'}`}>
                <Zap className="w-5 h-5" strokeWidth={2.5} />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-sm text-gray-900 flex items-center gap-2">
                  Stripe Connect
                  {isConnected && (
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#0055FF] text-white font-mono">
                      Connected
                    </span>
                  )}
                  {connect?.mocked && (
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-mono">
                      Beta · Mocked
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-600 mt-1">
                  {isConnected
                    ? `Account ${connect.stripe_account_id?.slice(0, 20)}… — you can receive payouts directly to your Australian bank account.`
                    : 'Connect Stripe to receive automatic payouts. Requires Level 1 (KYC) verification.'}
                </div>
                {isConnected && (
                  <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                    <span className={`px-1.5 py-0.5 rounded border ${connect.charges_enabled ? 'bg-blue-50 text-[#0044CC] border-blue-200' : 'bg-gray-50 text-gray-500 border-gray-200'}`}>
                      Charges {connect.charges_enabled ? 'enabled' : 'pending'}
                    </span>
                    <span className={`px-1.5 py-0.5 rounded border ${connect.payouts_enabled ? 'bg-blue-50 text-[#0044CC] border-blue-200' : 'bg-gray-50 text-gray-500 border-gray-200'}`}>
                      Payouts {connect.payouts_enabled ? 'enabled' : 'pending'}
                    </span>
                    <span className={`px-1.5 py-0.5 rounded border ${connect.details_submitted ? 'bg-blue-50 text-[#0044CC] border-blue-200' : 'bg-gray-50 text-gray-500 border-gray-200'}`}>
                      Details {connect.details_submitted ? 'submitted' : 'incomplete'}
                    </span>
                  </div>
                )}
              </div>
              {!isConnected && (
                <Button
                  onClick={startOnboarding}
                  disabled={connecting}
                  className="bg-[#0055FF] hover:bg-[#0044CC] text-white"
                  data-testid="stripe-onboard-btn"
                >
                  {connecting ? 'Redirecting…' : 'Connect Stripe'}
                  <ExternalLink className="w-3 h-3 ml-2" />
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-4 gap-px bg-gray-200 border border-gray-200 rounded-lg overflow-hidden mb-8">
              <StatBox label="Pending" value={`$${totalPending.toFixed(2)}`} icon={Clock} />
              <StatBox label="Transferred / Paid" value={`$${totalPaid.toFixed(2)}`} icon={CheckCircle2} />
              <StatBox label="Lifetime Gross" value={`$${(earnings?.gross_transferred_cents ?? 0).toFixed(2)}`} icon={DollarSign} note="from ledger" />
              <StatBox label="Reversed" value={`$${Math.abs(earnings?.reversed_cents ?? 0).toFixed(2)}`} icon={XCircle} />
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden" data-testid="payouts-table">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-left text-xs uppercase tracking-wider text-gray-500">
                    <th className="px-4 py-3">Payout ID</th>
                    <th className="px-4 py-3">Lead / Title</th>
                    <th className="px-4 py-3">Gross</th>
                    <th className="px-4 py-3">Fee</th>
                    <th className="px-4 py-3">Net</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {payouts.length === 0 ? (
                    <tr><td colSpan="7" className="text-center py-12 text-gray-500 text-sm">
                      No payouts yet. Sell leads to start accruing.
                    </td></tr>
                  ) : payouts.map(p => {
                    const cfg = PAYOUT_STATUS[p.status] || PAYOUT_STATUS.pending;
                    const Icon = cfg.icon;
                    return (
                      <tr key={p.payout_id} className="hover:bg-gray-50" data-testid={`payout-row-${p.payout_id}`}>
                        <td className="px-4 py-3 font-mono text-xs">{p.payout_id}</td>
                        <td className="px-4 py-3 font-mono text-xs">
                          <div>{p.lead_id}</div>
                          {p.digital_title && <div className="text-gray-400 text-[10px] mt-0.5">{p.digital_title}</div>}
                        </td>
                        <td className="px-4 py-3 font-mono">${p.gross_amount?.toFixed(2)}</td>
                        <td className="px-4 py-3 text-xs text-gray-500">{((p.platform_fee_pct || 0) * 100).toFixed(0)}%</td>
                        <td className="px-4 py-3 font-mono font-semibold">${p.net_amount?.toFixed(2)}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 border rounded ${cfg.color}`}>
                            <Icon className="w-3 h-3" />
                            {cfg.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-gray-500">
                          {new Date(p.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {user?.role === 'buyer' && (
          <>
            <h1 className="font-heading text-3xl font-semibold tracking-tight text-gray-900">Account Credit Notes</h1>
            <p className="text-sm text-gray-600 mt-1">
              Replacement credits issued when a dispute is resolved in your favour. Each credit is tied to the specific original purchase — not a stored wallet balance.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-gray-200 border border-gray-200 rounded-lg overflow-hidden mt-6 mb-8">
              <StatBox label="Outstanding Credits" value={`$${totalCredits.toFixed(2)}`} icon={Wallet} />
              <StatBox label="Total Issued" value={credits.length} icon={Receipt} />
            </div>

            {credits.length === 0 ? (
              <div className="border border-dashed border-gray-300 rounded-lg p-12 text-center text-gray-500 text-sm">
                No credit notes yet. Replacement credits are issued only after resolved disputes.
              </div>
            ) : (
              <div className="space-y-3" data-testid="credits-list">
                {credits.map(c => (
                  <div key={c.credit_id} className="bg-white border border-gray-200 rounded-lg p-5">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="font-mono text-xs text-gray-400">{c.credit_id}</div>
                        <div className="font-mono text-xs text-gray-500 mt-0.5">From purchase: {c.source_purchase_id}</div>
                      </div>
                      <StatusBadge status={c.status} />
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-sm text-gray-700">{c.reason}</span>
                      <span className="font-mono text-lg font-semibold">${c.amount.toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const StatBox = ({ label, value, icon: Icon, note }) => (
  <div className="bg-white p-5">
    <div className="flex items-center gap-2 mb-2">
      <Icon className="w-4 h-4 text-gray-400" />
      <span className="text-xs uppercase tracking-wider text-gray-500">{label}</span>
    </div>
    <div className="font-mono text-2xl font-semibold text-gray-900">{value}</div>
    {note && <div className="text-[10px] text-gray-400 mt-1 font-mono">{note}</div>}
  </div>
);
